// src/theme/theme.ts - REFACTORED FOR DARK MODE SUPPORT
import { Power } from '@mui/icons-material';
import { createTheme } from '@mui/material/styles';

// Extend the Color interface
declare module '@mui/material/styles' {
  interface Color {
    0?: string;
    750?: string;
  }
}

// SINGLE SOURCE OF TRUTH FOR ALL COLORS
export const colors = {
  primary: {
    light: '#bac8de',
    main: '#2c5aa0',
    dark: '#1c2e4a',
  },
  secondary: {
    light: '#f8dddd',
    main: '#ac3939',
    dark: '#450d0d',
  },
  tertiary: {
    light: '#b6bcc9',
    main: '#656e81',
    dark: '#333c4d',
  },
  text: {
    light: '#F0f8FF',
    dark: '#222222',
  },
  background: {
    light: '#FAFFFF',
    dark: '#1a1a1a',
    paperLight: '#ffffff',
    paperDark: '#2d2d2d',
  },
  header: {
    light: '#333',
    dark: '#2d2d2d', // Slightly lighter for dark mode
  },
  success: {
    main: '#4CAF50',
    dark: '#2a602a',
    light: '#82e0aa',
  },
  warning: {
    main: '#f39c12',
    dark: '#d68910',
    light: '#f8c471',
  },
  error: {
    main: '#a63f59',
    dark: '#3d1420',
    light: '#f4dde6',
  },
  info: {
    main: '#2c5aa0',
    dark: '#1c2e4a',
    light: '#bac8de',
  },
  grey: {
    0: '#ffffff',
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    750: '#525252',
    800: '#424242',
    900: '#212426',
  },
  alternatingRows: {
    lightOdd: '#ffffff',
    lightEven: '#e0e0e0',
    darkOdd: '#616161',
    darkEven: '#424242',
  },
  dividerContrast: {
    light: '#808080ff', // colors.grey[600] - Dark grey for light theme
    dark: 'rgba(255, 255, 255, 0.25)', // Lighter opacity for dark theme
  },
  gradients: {
    primary: '',
    secondary: '',
    tertiary: '',
    primaryToSecondary: '',
    secondaryToTertiary: '',
    light: '',
    white: 'linear-gradient(135deg, #FAFFFF 0%, #ffffff 100%)',
    pageBG: '',
    surface: '',
  },
  shadows: {
    light: '0 2px 4px rgba(28, 46, 74, 0.1)',
    medium: '0 4px 8px rgba(28, 46, 74, 0.1)',
    heavy: '0 8px 24px rgba(28, 46, 74, 0.15)',
    hover: '0 8px 16px rgba(28, 46, 74, 0.2)',
    focus: '0 0 0 3px rgba(44, 90, 160, 0.3)',
    power: '0 2px 4px 0 rgba(0, 0, 0, 0.2)',
    powerBold: '0 2px 4px 0 rgba(0, 0, 0, 0.7)',
    powerInset: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.2)',
    powerInsetBolder: 'inset 0 1px 3px 0 rgba(0, 0, 0, 0.4)',
    powerInsetBold: 'inset 0 0 4px 0 rgba(0, 0, 0, 0.9)',
    powerUp: '0 -3px 6px 0 rgba(0, 0, 0, 0.3)',
  },
  accessibility: {
    focusOutline: 'none',
    focusOffset: '0px',
    minTouchTarget: '44px',
    highContrastBorder: '#222222',
    focusColor: 'transparent',
    hover: {
      inputRingColor: { light: '', dark: '' },
      inputBackgroundColor: { light: '', dark: '' },
      inputTextColor: { light: '', dark: '' },
      radioBackgroundColor: { light: '', dark: '' },
      radioColor: { light: '', dark: '' },
      radioBorderRadius: '49%',
      toggleBackgroundColor: { light: '', dark: '' },
      toggleDotExpandScale: 1.15,
    },
    focus: {
      ringColor: { light: '', dark: '' },
      ringWidth: '3px',
    },
  },
};

// BASE COLOURS AND GRADIENTS
colors.gradients = {
  primary: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
  secondary: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
  tertiary: `linear-gradient(135deg, ${colors.tertiary.main} 0%, ${colors.tertiary.dark} 100%)`,
  primaryToSecondary: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
  secondaryToTertiary: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.tertiary.main} 100%)`,
  light: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.secondary.light} 100%)`,
  white: 'linear-gradient(135deg, #FAFFFF 0%, #ffffff 100%)',
  pageBG: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.tertiary.light} 100%)`,
  surface: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
};

// Accessibility Mode hover tokens - default look for the bolder hover state.
// Computed after colors exists so they stay derived from the same source colors.
colors.accessibility.hover = {
  inputRingColor: { light: colors.primary.main, dark: colors.primary.light },
  inputBackgroundColor: {
    light: `${colors.primary.light}70`,
    dark: colors.primary.dark,
  },
  inputTextColor: { light: colors.primary.dark, dark: colors.primary.light },
  radioBackgroundColor: {
    light: colors.primary.main,
    dark: colors.primary.main,
  },
  radioColor: { light: colors.grey[0], dark: colors.grey[0] },
  radioBorderRadius: '999px',
  toggleBackgroundColor: {
    light: 'unset',
    dark: colors.secondary.dark,
  },
  toggleDotExpandScale: 1.15,
};

// Always-on keyboard focus ring tokens - independent of Accessibility Mode.
colors.accessibility.focus = {
  ringColor: {
    light: `${colors.primary.main}40`,
    dark: `${colors.primary.light}40`,
  },
  ringWidth: '3px',
};

// Create theme as a FUNCTION that accepts mode
const createAppTheme = (mode: 'light' | 'dark') => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        light: colors.primary.light,
        main: colors.primary.main,
        dark: colors.primary.dark,
        contrastText: colors.text.light,
      },
      secondary: {
        light: colors.secondary.light,
        main: colors.secondary.main,
        dark: colors.secondary.dark,
        contrastText: colors.text.light,
      },
      tertiary: {
        light: colors.tertiary.light,
        main: colors.tertiary.main,
        dark: colors.tertiary.dark,
        contrastText: colors.text.light,
      },
      error: {
        main: colors.error.main,
        dark: colors.error.dark,
        light: colors.error.light,
        contrastText: colors.text.light,
      },
      warning: {
        main: colors.warning.main,
        dark: colors.warning.dark,
        light: colors.warning.light,
        contrastText: colors.text.dark,
      },
      success: {
        main: colors.success.main,
        dark: colors.success.dark,
        light: colors.success.light,
        contrastText: colors.text.light,
      },
      info: {
        main: colors.info.main,
        dark: colors.info.dark,
        light: colors.info.light,
        contrastText: colors.text.light,
      },
      background: {
        default: isDark ? colors.background.dark : colors.background.light,
        paper: isDark
          ? colors.background.paperDark
          : colors.background.paperLight,
      },
      text: {
        primary: isDark ? colors.text.light : colors.text.dark,
        secondary: colors.tertiary.main,
        disabled: colors.tertiary.light,
      },
      alternatingRows: colors.alternatingRows,
      grey: colors.grey,
      dividerContrast: isDark
        ? colors.dividerContrast.dark
        : colors.dividerContrast.light,
    },

    typography: {
      fontSize: 18,
      fontFamily: ['Segoe UI', 'Roboto', 'Arial', 'sans-serif'].join(','),
      fontWeightLight: 400,
      fontWeightRegular: 500,
      fontWeightMedium: 600,
      fontWeightBold: 700,
      h1: {
        fontSize: '3.5rem',
        fontWeight: 900,
        lineHeight: 1.2,
        letterSpacing: '0.02em',
      },
      h2: {
        fontSize: '2.8rem',
        fontWeight: 800,
        lineHeight: 1.3,
        letterSpacing: '0.01em',
      },
      h3: {
        fontSize: '2.2rem',
        fontWeight: 700,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: '1.8rem',
        fontWeight: 700,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: '1.3rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1.2rem',
        fontWeight: 500,
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '1.1rem',
        fontWeight: 500,
        lineHeight: 1.6,
      },
      button: {
        fontSize: '1.1rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      },
      caption: {
        fontSize: '1rem',
        fontWeight: 500,
      },
    },

    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            fontSize: '1.2rem',
            fontWeight: 700,
            padding: '12px 24px',
            borderRadius: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            minHeight: '48px',
            minWidth: '120px',
            boxShadow: colors.shadows.light,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: colors.shadows.hover,
            },
            '&:focus': {
              outline: colors.accessibility.focusOutline,
              outlineOffset: colors.accessibility.focusOffset,
            },
          },
          containedPrimary: {
            backgroundColor: colors.primary.main,
            color: colors.text.light,
            '&:hover': {
              backgroundColor: colors.primary.dark,
            },
          },
          containedSecondary: {
            // This will now automatically use the mode-specific secondary.main
            backgroundColor: isDark
              ? colors.secondary.main
              : colors.secondary.main,
            color: colors.text.light,
            '&:hover': {
              backgroundColor: colors.secondary.dark,
            },
          },
          sizeLarge: {
            fontSize: '1.4rem',
            padding: '16px 32px',
            minHeight: '56px',
          },
          sizeSmall: {
            fontSize: '1rem',
            padding: '8px 16px',
            minHeight: '40px',
          },
        },
      },

      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-root': {
              fontSize: '1.2rem',
              fontWeight: 500,
            },
            '& .MuiInputLabel-root': {
              fontSize: '1.1rem',
              fontWeight: 600,
              color: colors.tertiary.main,
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderWidth: '2px',
                borderColor: colors.primary.light,
              },
              '&:hover fieldset': {
                borderColor: colors.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderWidth: '3px',
                borderColor: colors.primary.main,
              },
            },
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            boxShadow: colors.shadows.medium,
            border: `2px solid ${colors.primary.light}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: colors.shadows.heavy,
              borderColor: colors.tertiary.light,
            },
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: '1.1rem',
            fontWeight: 500,
            padding: '16px',
            borderBottom: `2px solid ${colors.primary.light}`,
          },
          head: {
            fontSize: '1.2rem',
            fontWeight: 700,
            backgroundColor: colors.tertiary.main,
            color: colors.text.light,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '16px',
            padding: '20px',
          },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: {
            // MODE-SPECIFIC: Different header colors for light/dark
            backgroundColor: isDark ? colors.header.dark : colors.header.light,
            boxShadow: colors.shadows.medium,
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            fontSize: '1rem',
            fontWeight: 600,
            height: '36px',
          },
          colorPrimary: {
            backgroundColor: colors.primary.light,
            color: colors.primary.dark,
          },
          colorSecondary: {
            backgroundColor: colors.secondary.light,
            color: colors.secondary.dark,
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          elevation1: {
            boxShadow: colors.shadows.light,
          },
          elevation2: {
            boxShadow: colors.shadows.medium,
          },
          elevation3: {
            boxShadow: colors.shadows.heavy,
          },
        },
      },
    },

    spacing: 8,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    shape: {
      borderRadius: 8,
    },
  });
};

// Export the function as default
export default createAppTheme;

// Also export a default light theme for backwards compatibility
export const lightTheme = createAppTheme('light');
export const darkTheme = createAppTheme('dark');
